import React, { useState } from "react";
import { Sparkles, Truck, Receipt, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase POLISH-1 #3 — One-step welcome tour for customers who just
 * redeemed a ``customer_portal`` invite. Renders only while the
 * AuthContext's user carries ``welcome_tour_pending === true``.
 *
 * Light-weight: no tour library, just a card + two highlighted callouts
 * pointing at the existing dashboard sections. "Got it" calls
 * /api/customer/welcome-tour/dismiss and refreshes the auth context so
 * the card disappears for the rest of the session AND on next sign-in.
 */
export function CustomerWelcomeCard() {
    const { user, refreshUser } = useAuth();
    const [dismissing, setDismissing] = useState(false);
    const [hidden, setHidden] = useState(false);
    if (!user || !user.welcome_tour_pending || hidden) return null;

    const onDismiss = async () => {
        setDismissing(true);
        try {
            await api.post("/customer/welcome-tour/dismiss");
            await refreshUser();
        } catch {
            // Soft-fail — hide locally so the card doesn't get stuck if
            // the network is flaky; the server flag will be cleared on
            // the next successful call.
        }
        setHidden(true);
    };

    return (
        <div
            className="mt-6 relative rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-6 sm:p-8 shadow-sm"
            data-testid="customer-welcome-tour-card"
        >
            <button
                type="button"
                aria-label="Dismiss welcome tour"
                onClick={onDismiss}
                disabled={dismissing}
                className="absolute top-3 right-3 text-slate-400 hover:text-slate-700 transition-colors"
                data-testid="customer-welcome-tour-close-x"
            >
                <X size={16} />
            </button>
            <div className="flex items-start gap-4">
                <div className="hidden sm:flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-900">
                    <Sparkles size={22} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Welcome aboard</p>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-1.5">
                        Welcome to your rental portal
                    </h2>
                    <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-2xl">
                        Your account is ready. Here's where to find things:
                    </p>

                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                        <div
                            className="flex items-start gap-3 rounded-lg border border-blue-100 bg-white px-4 py-3"
                            data-testid="customer-welcome-tour-callout-rentals"
                        >
                            <Truck size={18} className="text-blue-900 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="font-semibold text-sm text-slate-900">My rentals</p>
                                <p className="text-xs text-slate-600 mt-0.5">
                                    See equipment you're currently leasing — scroll down to find the list on this page.
                                </p>
                            </div>
                        </div>
                        <div
                            className="flex items-start gap-3 rounded-lg border border-blue-100 bg-white px-4 py-3"
                            data-testid="customer-welcome-tour-callout-invoices"
                        >
                            <Receipt size={18} className="text-blue-900 shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <p className="font-semibold text-sm text-slate-900">My invoices</p>
                                <p className="text-xs text-slate-600 mt-0.5">
                                    View and pay balances. Open from the top nav or the invoices section below.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-5 flex items-center gap-3">
                        <Button
                            onClick={onDismiss}
                            disabled={dismissing}
                            className="bg-blue-900 hover:bg-blue-800 text-white"
                            data-testid="customer-welcome-tour-dismiss-button"
                        >
                            {dismissing ? "Saving…" : "Got it"}
                        </Button>
                        <span className="text-xs text-slate-500">You won't see this again.</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CustomerWelcomeCard;
